import React, { useCallback, useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Pill from 'LuxKit/Pill'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferBookButton from '../../OfferBookButtons/OfferBookButton'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable/Clickable'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { max, unique } from 'lib/array/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import TextLink from 'LuxKit/TextLink'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import Label from 'LuxKit/Label'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPageBNBLcta from 'components/OfferPage/OfferPageBNBLcta'
import OfferCalendarAirportModal from 'components/OfferPage/Filters/CalendarPicker/CalendarAirport/OfferCalendarAirportModal'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from 'components/OfferPage/OfferBookButtons/OfferSelectPackageButton'
import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import useLoyaltyOfferPageTargetRoomUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetRoomUpgrade'
import ContactContext from 'contexts/contactContext'
interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  fetching?: boolean;
  currency: string;
  isSpoofed: boolean;
  defaultDepositAmountPercentage: number;
  inclusionsViewableLuxPlusTier: App.MembershipSubscriptionTier;
  userMultiItemCartEnabled: boolean;
  isFlightsSelected?: boolean;
}

function FlashHotelOptionPriceDetailsVariant(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    fetching,
    currency,
    isSpoofed,
    defaultDepositAmountPercentage,
    userMultiItemCartEnabled,
    isFlightsSelected,
    packageView,
  } = props

  const {
    package: pkg,
    flightPrice,
    pricingWithFlights,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    flightsAvailable,
    hotelPrice,
    propertyFees,
    taxesAndFees,
    hotelTotal,
    hotelMemberTotal,
    pricing,
    memberPricing,
  } = packageView

  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const showModal = useContext(ModalContext)
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (flightsAvailable || offer.bundledWithFlightsOnly)
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const extraGuestSurchargesPayableAtProperty = !!pkg.roomRate?.extraGuestSurcharges.length && pkg.roomRate?.extraGuestSurcharges.some(surcharge => surcharge?.payableAtProperty)

  const showSelectAirportModal = useCallback(() => {
    showModal(<OfferCalendarAirportModal />)
  }, [showModal])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = offer.bundledWithFlightsOnly ? bundledDiscountPercent : max([hotelDiscountPercent, bundledDiscountPercent])

  const contact = useContext(ContactContext)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330
  }, [checkInMoment])

  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0 || config.agentHub.isEnabled
  const showMemberPrice = !!memberPricing && memberPricing.price > 0

  const totalMemberPlusFlightsPrice = hotelMemberTotal > 0 ? hotelMemberTotal + flightPrice : 0
  const totalPlusFlightsPrice = hotelTotal + flightPrice

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetRoomUpgrade(pkg)
  const showBNBL = !selectedTargetUpgradePackage && packageView.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && config.accommodation.hasBnbl
  const canAddToUserCart = canBuyNow && userMultiItemCartEnabled && !selectedTargetUpgradePackage
  const hasFlightWithCachePrice = !!flightPrice && !offer.flightCacheDisabled
  const shouldShowDiscount = !offer.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (offer.bundledWithFlightsOnly ? hasFlightWithCachePrice : true)
  const shouldShowValue = hotelValue > hotelPrice && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, hotelPrice)
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const showReserveForZero = isSpoofed && roomRateIsReservableForZero
  const showDepositTag = isDepositEnabled && !isSpoofed
  const showFlexiblePaymentTag = isDepositEnabled && isSpoofed
  const showFlightsDealTag = flightsEnabled && flightOrigin && flightDestination && deals.length > 0

  const hotelAndFlightsToggleEnabled = flightsEnabled && !offer.bundledWithFlightsOnly
  const hideHotelPriceBlock = hotelAndFlightsToggleEnabled && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = hotelAndFlightsToggleEnabled && !isFlightsSelected

  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture

  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      const currentPrice = isFlightsSelected ? pricingWithFlights?.price : pricing.price

      if (currentPrice) {
        Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id, includesFlights: isFlightsSelected, packageOptionPrice: currentPrice }))
      }
    } else {
      trackEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, isFlightsSelected, offer, pkg.roomRate?.id, pricing.price, pricingWithFlights?.price, trackEvent])

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])
  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])

  const otherFees = {
    propertyFees: packageView.propertyFees,
    extraGuestsSurcharge: extraGuestSurchargesPayableAtProperty ? packageView.extraGuestsSurcharge : 0,
  }

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  return <>
    <Group direction="horizontal" gap={8} wrap="wrap" noDisplayWhenEmpty>
      {shouldShowDiscount && !!discountPercent && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={discountPercent}
      />}
      {showDepositTag && <SecureWithDepositTagAndModal
        isFlexibleCancellation={isFlexibleCancellation}
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
      />}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}>
        <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
          Flexible payment options
        </Pill>
      </Clickable>}

    </Group>
    <AccommodationPackageTilePricingBlocks>
      {!offer.bundledWithFlightsOnly && !hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="pricing"
        title={flightsEnabled && !hotelAndFlightsToggleEnabled ? 'Hotel' : undefined}
        callToAction={<Group direction="horizontal" horizontalAlign="stretch" gap={8}>
          {canAddToUserCart && <OfferAddToCartButton
            size="large"
            packageView={packageView}
            type="submit"
            disabled={fetching}
          />}
          <OfferBookButton
            size="large"
            type="submit"
            data-testid="HotelBookingBtn"
            disabled={fetching}
            packageView={packageView}
            onSubmit={onSubmit}
          >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </OfferBookButton>
        </Group>}
      >
        <VerticalSpacer gap={4}>
          <div>
            <PriceRowPriceCaption>
              {pluralizeToString('night', pkg.duration)} {!checkIn && 'from'}
            </PriceRowPriceCaption>
            <LuxPlusPriceStack
              size="L"
              price={hotelTotal}
              memberPrice={hotelMemberTotal}
              saleUnit="total"
              testid="offer-package-price"
              offerConfig={offer.luxPlus}
            />
            <PriceRowTaxesAndFees />
            {showIncludedGuestsExceeded && <AdditionalGuestsPopup
              complex={false}
              description="+ Extra guest surcharge"
              modalContent={pkg.roomPolicyDescription}
            />}
            {shouldShowValue && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="M"
              originalValue={hotelValue}
            />}
            <BusinessTravellerOfferCreditsTextLink
              type="exact"
              offer={offer}
              totalCost={hotelTotal}
              numberOfNights={pkg.duration}
              roomCount={rooms?.length}
            />
          </div>
          <PriceRowAgentHubCommission
            offerId={offer.id}
            size="M"
            productType={offer.productType}
            kind="plain"
            packageId={pkg.id}
          />
          <div>
            {showPriceDetails && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              duration={pkg.duration}
              propertyFees={packageView.propertyFees}
              hotelPrice={packageView.hotelPrice}
              otherFees={otherFees}
              rooms={rooms}
              extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
              dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
              hotelMemberPrice={packageView.hotelMemberPrice}
              showMemberPrice={showMemberPrice}
            />}
            {showReserveForZero && <TextLink
              colour="highlight-secondary"
              size="small"
              weight="bold"
              endIcon={<LineInfoCircleIcon colour="primary" />}
              onClick={openReserveForZeroPolicyModal}
            >
              Reserve now for free
            </TextLink>}
          </div>
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}

      {flightsEnabled && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title={!flightsBundledNoCache && !hotelAndFlightsToggleEnabled ? 'Hotel + flights' : undefined}
        titleTrailingExperimental={<TextLink onClick={showSelectAirportModal}>({flightOrigin.code})</TextLink>}
        callToAction={<>
          {!hasFlights && flightOrigin.code === flightDestination?.code && <TextButton
            size="large"
            data-filter="airport"
            kind="tertiary"
            onClick={showSelectAirportModal}
          >
            Change airport
          </TextButton>}
          {flightsBookable && (!canBuyNow || !userMultiItemCartEnabled) && <OfferBookButton
            size="large"
            type="submit"
            data-testid="HotelAndFlightsBtn"
            disabled={fetching}
            packageView={packageView}
            data-buytype="flights"
            onSubmit={onSubmit}
          >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </OfferBookButton>}
          {flightsBookable && canAddToUserCart && <OfferSelectPackageButton
            size="large"
            type="submit"
            disabled={fetching}
            packageView={packageView}
            data-buytype="flights"
          />}
          {flightsBundledNoCache &&
            <Group direction="vertical" gap={12} horizontalAlign="center">
              <BodyText variant="small">OR</BodyText>
              <BodyText variant="medium" weight="bold">
                Call our concierge team for<br />exclusive prices & priority booking
              </BodyText>
              <TextButton
                size="large"
                kind="secondary"
                startIcon={<LinePhoneIcon />}
                href={`tel:${contact.defaultContact.number}`}
                rel="nofollow"
              >
                {contact.defaultContact.humanReadable}
              </TextButton>
            </Group>}
        </>}
      >
        {hasFlights && <>
          <PriceRowPriceCaption>
            {pluralizeToString('night', pkg.duration)} + flights from
          </PriceRowPriceCaption>
          <LuxPlusPriceStack
            size="L"
            price={totalPlusFlightsPrice}
            memberPrice={totalMemberPlusFlightsPrice}
            saleUnit="total*"
            testid="offer-package-price-with-flight"
            offerConfig={offer.luxPlus}
          />
          {!rooms && <PriceRowCaption>Two Adults</PriceRowCaption>}
          <PriceRowTaxesAndFees />
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description="+ Extra guest surcharge"
            modalContent={pkg.roomPolicyDescription}
          />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="M"
            originalValue={hotelValue + flightPrice}
          />}
          <BusinessTravellerOfferCreditsTextLink
            type="exact"
            offer={offer}
            totalCost={hotelTotal}
            numberOfNights={pkg.duration}
            roomCount={rooms?.length}
          />
          <PriceRowAgentHubCommission
            size="M"
            kind="plain"
            offerId={offer.id}
            productType={offer.productType}
            offerLocations={offerLocations}
            vendorName={vendorName}
            packageId={pkg.id}
          />
          {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
          {showPriceDetails && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={packageView.propertyFees}
            otherFees={otherFees}
            hotelPrice={packageView.hotelPrice}
            flightPrice={packageView.flightPrice}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
            hotelMemberPrice={packageView.hotelMemberPrice}
            showMemberPrice={showMemberPrice}
          />}
        </>}
        <AtolProtectedTooltip />
      </AccommodationPackageTilePricingBlock>}
    </AccommodationPackageTilePricingBlocks>
    <LoadingIndicator visible={fetching} floating opaque />
    {showBNBL && <OfferPageBNBLcta packageView={packageView} direction="horizontal" />}
  </>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  isSpoofed: appState.auth.account.isSpoofed,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(appState),
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  isFlightsSelected: state.flights,
}), FlashHotelOptionPriceDetailsVariant)
